/**
 * Customer Insight Agent
 * Analyzes customer preferences, provides personalized suggestions
 */

interface CustomerProfile {
  id: string;
  name: string;
  preferences?: string[];
  searchHistory?: string[];
  purchaseHistory?: { product: string; date: Date; farm: string }[];
  location?: { lat: number; lng: number };
  dietary?: string[];
  budget?: { min: number; max: number };
}

interface CustomerSegment {
  name: string;
  description: string;
  customerCount: number;
  characteristics: string[];
  preferredProducts: string[];
  averageBudget: number;
  topFarms: string[];
}

interface PersonalizedRecommendation {
  customerId: string;
  itemType: 'product' | 'farm' | 'recipe';
  recommendation: string;
  reason: string;
  confidence: number;
  relevanceScore: number;
}

interface CustomerBehavior {
  totalSearches: number;
  totalPurchases: number;
  averageOrderValue: number;
  preferredCategories: string[];
  seasonalBehavior: { month: number; activity: number }[];
  loyaltyScore: number;
}

class CustomerInsightAgent {
  private customerProfiles: Map<string, CustomerProfile> = new Map();

  /**
   * Create or update customer profile
   */
  updateCustomerProfile(profile: CustomerProfile): void {
    this.customerProfiles.set(profile.id, profile);
  }

  /**
   * Record customer search
   */
  recordSearch(customerId: string, searchQuery: string): void {
    const profile = this.customerProfiles.get(customerId);
    if (profile) {
      if (!profile.searchHistory) {
        profile.searchHistory = [];
      }
      profile.searchHistory.push(searchQuery);
    }
  }

  /**
   * Record customer purchase
   */
  recordPurchase(
    customerId: string,
    product: string,
    farm: string
  ): void {
    const profile = this.customerProfiles.get(customerId);
    if (profile) {
      if (!profile.purchaseHistory) {
        profile.purchaseHistory = [];
      }
      profile.purchaseHistory.push({
        product,
        date: new Date(),
        farm,
      });
    }
  }

  /**
   * Analyze customer behavior
   */
  analyzeCustomerBehavior(customerId: string): CustomerBehavior {
    const profile = this.customerProfiles.get(customerId);

    if (!profile) {
      return {
        totalSearches: 0,
        totalPurchases: 0,
        averageOrderValue: 0,
        preferredCategories: [],
        seasonalBehavior: [],
        loyaltyScore: 0,
      };
    }

    const totalSearches = profile.searchHistory?.length || 0;
    const totalPurchases = profile.purchaseHistory?.length || 0;

    // Calculate preferred categories from purchase history
    const categoryCount: { [key: string]: number } = {};
    profile.purchaseHistory?.forEach((purchase) => {
      const category = this.categorizeProduct(purchase.product);
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    });

    const preferredCategories = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([cat]) => cat);

    // Analyze seasonal behavior
    const seasonalBehavior = this.analyzeSeasonalBehavior(profile);

    // Calculate loyalty score
    const loyaltyScore = this.calculateLoyaltyScore(profile);

    // Calculate average order value
    const farmFrequency: { [key: string]: number } = {};
    profile.purchaseHistory?.forEach((purchase) => {
      farmFrequency[purchase.farm] = (farmFrequency[purchase.farm] || 0) + 1;
    });

    return {
      totalSearches,
      totalPurchases,
      averageOrderValue: totalPurchases > 0 ? 25 : 0, // Simplified
      preferredCategories,
      seasonalBehavior,
      loyaltyScore,
    };
  }

  /**
   * Get personalized recommendations for customer
   */
  getPersonalizedRecommendations(
    customerId: string,
    allProducts: string[],
    allFarms: { id: string; name: string; products: string[] }[]
  ): PersonalizedRecommendation[] {
    const profile = this.customerProfiles.get(customerId);

    if (!profile) {
      return [];
    }

    const recommendations: PersonalizedRecommendation[] = [];

    // Product recommendations based on preferences
    if (profile.preferences && profile.preferences.length > 0) {
      const recommendedProducts = this.findSimilarProducts(
        profile.preferences,
        allProducts,
        profile.purchaseHistory?.map((p) => p.product) || []
      );

      recommendedProducts.forEach((product, index) => {
        recommendations.push({
          customerId,
          itemType: 'product',
          recommendation: product,
          reason: `Similar to your preferred items`,
          confidence: Math.max(0, 90 - index * 10),
          relevanceScore: 85,
        });
      });
    }

    // Farm recommendations based on purchase history
    if (profile.purchaseHistory && profile.purchaseHistory.length > 0) {
      const recommendedFarms = this.findComplementaryFarms(
        profile,
        allFarms
      );

      recommendedFarms.forEach((farm, index) => {
        recommendations.push({
          customerId,
          itemType: 'farm',
          recommendation: farm.name,
          reason: `Carries products you enjoy`,
          confidence: Math.max(0, 85 - index * 10),
          relevanceScore: 80,
        });
      });
    }

    // Sort by relevance and confidence
    recommendations.sort(
      (a, b) =>
        b.relevanceScore * (b.confidence / 100) -
        a.relevanceScore * (a.confidence / 100)
    );

    return recommendations.slice(0, 5);
  }

  /**
   * Segment customers
   */
  segmentCustomers(customers: CustomerProfile[]): CustomerSegment[] {
    const segments: CustomerSegment[] = [];

    // Segment 1: Health-conscious buyers
    const healthConscious = customers.filter((c) =>
      c.dietary?.some((d) => d.toLowerCase().includes('organic'))
    );
    if (healthConscious.length > 0) {
      segments.push({
        name: 'Health-Conscious Buyers',
        description:
          'Customers focused on organic and sustainable products',
        customerCount: healthConscious.length,
        characteristics: ['Organic preference', 'Health focus', 'Quality over price'],
        preferredProducts: ['Organic vegetables', 'Herbs', 'Sustainable items'],
        averageBudget: 75,
        topFarms: this.getTopFarmsForSegment(healthConscious),
      });
    }

    // Segment 2: Budget-conscious buyers
    const budgetConscious = customers.filter((c) =>
      c.budget ? c.budget.max < 30 : false
    );
    if (budgetConscious.length > 0) {
      segments.push({
        name: 'Budget-Conscious Buyers',
        description: 'Price-sensitive customers looking for value',
        customerCount: budgetConscious.length,
        characteristics: ['Price sensitive', 'Volume focused', 'Bulk purchases'],
        preferredProducts: ['Bulk vegetables', 'Seasonal items', 'Discounted products'],
        averageBudget: 20,
        topFarms: this.getTopFarmsForSegment(budgetConscious),
      });
    }

    // Segment 3: Specialty seekers
    const specialtySeeker = customers.filter((c) =>
      c.dietary ? c.dietary.length > 0 : false
    );
    if (specialtySeeker.length > 0) {
      segments.push({
        name: 'Specialty Seekers',
        description: 'Customers with specific dietary needs',
        customerCount: specialtySeeker.length,
        characteristics: ['Dietary needs', 'Quality conscious', 'Willing to pay'],
        preferredProducts: ['Specialty items', 'Vegan options', 'Allergen-free'],
        averageBudget: 60,
        topFarms: this.getTopFarmsForSegment(specialtySeeker),
      });
    }

    // Segment 4: Regular customers
    const regularCustomers = customers.filter(
      (c) =>
        c.purchaseHistory && c.purchaseHistory.length > 5
    );
    if (regularCustomers.length > 0) {
      segments.push({
        name: 'Regular Customers',
        description: 'Loyal repeat customers',
        customerCount: regularCustomers.length,
        characteristics: ['High loyalty', 'Consistent purchases', 'Reliable demand'],
        preferredProducts: ['Staple items', 'Seasonal favorites', 'Premium products'],
        averageBudget: 50,
        topFarms: this.getTopFarmsForSegment(regularCustomers),
      });
    }

    return segments;
  }

  /**
   * Identify churn risk
   */
  identifyChurnRisk(customerId: string): {
    riskLevel: 'low' | 'medium' | 'high';
    riskScore: number;
    indicators: string[];
    recommendations: string[];
  } {
    const profile = this.customerProfiles.get(customerId);

    if (!profile) {
      return {
        riskLevel: 'high',
        riskScore: 100,
        indicators: ['Customer not found'],
        recommendations: ['Create customer profile'],
      };
    }

    let riskScore = 0;
    const indicators: string[] = [];

    // Check purchase frequency
    const daysSinceLastPurchase = profile.purchaseHistory
      ? this.getDaysSinceLastPurchase(profile.purchaseHistory)
      : 999;

    if (daysSinceLastPurchase > 90) {
      riskScore += 40;
      indicators.push('No recent purchases (90+ days)');
    } else if (daysSinceLastPurchase > 30) {
      riskScore += 20;
      indicators.push('Low purchase frequency');
    }

    // Check engagement
    const totalEngagement =
      (profile.searchHistory?.length || 0) + (profile.purchaseHistory?.length || 0);
    if (totalEngagement === 0) {
      riskScore += 40;
      indicators.push('No engagement activity');
    } else if (totalEngagement < 3) {
      riskScore += 20;
      indicators.push('Minimal engagement');
    }

    // Check customer lifetime value
    if (profile.purchaseHistory && profile.purchaseHistory.length < 2) {
      riskScore += 10;
      indicators.push('Low customer lifetime value');
    }

    // Determine risk level
    let riskLevel: 'low' | 'medium' | 'high' = 'low';
    if (riskScore >= 60) {
      riskLevel = 'high';
    } else if (riskScore >= 30) {
      riskLevel = 'medium';
    }

    // Generate recommendations
    const recommendations: string[] = [];
    if (riskLevel === 'high') {
      recommendations.push('Send personalized re-engagement offer');
      recommendations.push('Highlight new products matching their interests');
      recommendations.push('Offer loyalty rewards');
    } else if (riskLevel === 'medium') {
      recommendations.push('Send product recommendations');
      recommendations.push('Notify about seasonal items');
      recommendations.push('Encourage farm visits');
    }

    return {
      riskLevel,
      riskScore,
      indicators,
      recommendations,
    };
  }

  /**
   * Categorize product
   */
  private categorizeProduct(product: string): string {
    const lowercased = product.toLowerCase();

    if (
      ['tomato', 'pepper', 'cucumber', 'lettuce', 'spinach'].some((v) =>
        lowercased.includes(v)
      )
    ) {
      return 'Vegetables';
    }
    if (
      ['apple', 'orange', 'berry', 'strawberry', 'avocado'].some((v) =>
        lowercased.includes(v)
      )
    ) {
      return 'Fruits';
    }
    if (['herb', 'basil', 'cilantro', 'parsley'].some((v) =>
      lowercased.includes(v)
    )) {
      return 'Herbs';
    }
    return 'Other';
  }

  /**
   * Analyze seasonal behavior
   */
  private analyzeSeasonalBehavior(profile: CustomerProfile): { month: number; activity: number }[] {
    const seasonalActivity: { [month: number]: number } = {};

    profile.purchaseHistory?.forEach((purchase) => {
      const month = purchase.date.getMonth() + 1;
      seasonalActivity[month] = (seasonalActivity[month] || 0) + 1;
    });

    return Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      activity: seasonalActivity[i + 1] || 0,
    }));
  }

  /**
   * Calculate loyalty score
   */
  private calculateLoyaltyScore(profile: CustomerProfile): number {
    let score = 0;

    // Purchase frequency
    const purchases = profile.purchaseHistory?.length || 0;
    score += Math.min(40, purchases * 5);

    // Return customer bonus
    if (purchases > 1) {
      score += 20;
    }

    // Farm diversity
    const uniqueFarms = new Set(
      profile.purchaseHistory?.map((p) => p.farm) || []
    ).size;
    score += Math.min(20, uniqueFarms * 5);

    // Engagement
    const searches = profile.searchHistory?.length || 0;
    score += Math.min(20, searches * 2);

    return Math.min(100, score);
  }

  /**
   * Find similar products
   */
  private findSimilarProducts(
    preferences: string[],
    allProducts: string[],
    purchasedProducts: string[]
  ): string[] {
    return allProducts
      .filter((product) => !purchasedProducts.includes(product))
      .filter((product) =>
        preferences.some((pref) =>
          product.toLowerCase().includes(pref.toLowerCase())
        )
      )
      .slice(0, 3);
  }

  /**
   * Find complementary farms
   */
  private findComplementaryFarms(
    profile: CustomerProfile,
    allFarms: { id: string; name: string; products: string[] }[]
  ): { id: string; name: string; products: string[] }[] {
    const visitedFarms = new Set(
      profile.purchaseHistory?.map((p) => p.farm) || []
    );

    return allFarms
      .filter((farm) => !visitedFarms.has(farm.name))
      .filter((farm) =>
        farm.products.some((product) =>
          profile.preferences?.some((pref) =>
            product.toLowerCase().includes(pref.toLowerCase())
          )
        )
      )
      .slice(0, 3);
  }

  /**
   * Get top farms for segment
   */
  private getTopFarmsForSegment(customers: CustomerProfile[]): string[] {
    const farmFrequency: { [key: string]: number } = {};

    customers.forEach((customer) => {
      customer.purchaseHistory?.forEach((purchase) => {
        farmFrequency[purchase.farm] =
          (farmFrequency[purchase.farm] || 0) + 1;
      });
    });

    return Object.entries(farmFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([farm]) => farm);
  }

  /**
   * Get days since last purchase
   */
  private getDaysSinceLastPurchase(
    purchases: { product: string; date: Date; farm: string }[]
  ): number {
    if (purchases.length === 0) return 999;
    const lastPurchase = new Date(
      Math.max(...purchases.map((p) => p.date.getTime()))
    );
    const today = new Date();
    return Math.floor((today.getTime() - lastPurchase.getTime()) / (1000 * 60 * 60 * 24));
  }
}

export default new CustomerInsightAgent();
